import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { useSelector } from 'react-redux';
import { InstallmentStatus, Invoice, PaymentInstallment } from '#types';
import { FlattenedUser } from '../../types/user';
import { segmentEvent, SENT_INVOICE } from './segment';
import rpcShared from '@rockpapercoin/rpc-shared';

const sortByDueDate = (
	a: { dueDate?: Date | string | null },
	b: { dueDate?: Date | string | null }
) => {
	const toDate = ( value: Date | string ) =>
		typeof value === 'string' ? new Date( value ) : value;
	if ( ( !a || !a.dueDate ) && ( !b || !b.dueDate ) ) {
		return 0;
	} else if ( ( !a || !a.dueDate ) && b?.dueDate ) {
		return -1;
	} else if ( a?.dueDate && ( !b || !b.dueDate ) ) {
		return 1;
	} else if ( a?.dueDate && b?.dueDate ) {
		const A = toDate( a.dueDate ).getTime();
		const B = toDate( b.dueDate ).getTime();
		if ( A > B ) return 1;
		if ( A < B ) return -1;
	}
	return 0;
};

type MinimumSegmentInvoiceData = Partial<Pick<Invoice, 'totalAmount'>> & {
	paymentInstallments?: Pick<PaymentInstallment, 'dueDate' | 'status'>[] | null;
} & Parameters<typeof rpcShared.invoices.calculateInvoiceTotal>[0];

// A helper to return a callback we can use when we want to track an invoice-sent event in segment
export const useTrackInvoiceSent = ( isResend: boolean ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { pathname } = useBlockingRouter();
	if ( !isResend ) {
		/* sometimes we don't know when we load the hook if the invoice is sent or not, like on `/invoices`. So there pass
		in true when setting up the hook, and then false as the second param here to skip the send if the invoice in
		question has already been sent */
		return async <T>(
			invoice?: ( T extends MinimumSegmentInvoiceData ? T : never ) | null,
			skipSend?: boolean
		) => {
			if ( invoice && !skipSend ) {
				// first lets try to populate totalAmount if it's missing, as the segment method wants that
				if ( !invoice.totalAmount ) {
					invoice.totalAmount =
						rpcShared.invoices.calculateInvoiceTotal( invoice );
				}
				/* now lets depopulate the payment installments so as to make the only installments unpaid ones
				so we're actually reporting the next due and not possibly an offline payment */
				invoice.paymentInstallments = invoice.paymentInstallments
					?.sort( sortByDueDate )
					.filter(
						( paymentInstallment ) =>
							paymentInstallment.status === InstallmentStatus.Unpaid
					);
				const segmentPayload = {
					invoice,
					page: pathname,
					user: user && 'user' in user ? user.user : undefined,
				};
				await segmentEvent( SENT_INVOICE, segmentPayload );
			}
		};
	} else {
		return () => Promise.resolve();
	}
};
